// Angular
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';

// Material
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

/**
 * Payload accepted by {@link MatNotificationService.showAlert} and
 * forwarded as `MAT_DIALOG_DATA` to {@link MatDialogAlertComponent}.
 * Field names are kept identical to the legacy contract so every
 * existing caller (bet-service, bet-preview, wager, bets, runner,
 * dutch, abc, app shell, etc.) keeps working without any code change.
 */
export interface MatDialogAlertData {
  /** Bold uppercase title shown in the blue header (e.g. "Error"). */
  title: string;
  /** Optional one-line subtitle rendered between header and message. */
  subtitle?: string;
  /** Body copy. Newlines are preserved as visual line breaks. */
  message: string;
  /**
   * Label for the primary action button. Closes the dialog with
   * `true`. Omit to hide the primary button entirely.
   */
  actionPositiveText?: string;
  /**
   * Label for the secondary / dismiss button. Closes with `undefined`
   * (mapped to `false` by MatNotificationService). Omit to hide it.
   */
  actionNegativeText?: string;
  /**
   * Arbitrary payload echoed by callers. Kept as `any` to preserve
   * the legacy MatNotificationService.showAlert signature.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: any;
}

/**
 * Severity classification derived purely from the title. Used to pick
 * a leading icon in the header so the user can tell at a glance
 * whether the dialog is informational, a success notice, a warning,
 * or an error — without changing the dialog's overall blue branding
 * (which keeps it visually consistent with `WagerConfirmDialog` and
 * `MatConfirmDialog`).
 */
type DialogSeverity = 'error' | 'success' | 'warning' | 'info';

/**
 * Shared alert dialog used by {@link MatNotificationService.showAlert}.
 * Restyled to match the visual language of `WagerConfirmDialog`
 * ("Place Wager" / "Save Wager") and `MatConfirmDialog`
 * (geolocation): a solid blue rounded header with the title and an
 * X icon, a centered white body, and a footer with rounded pill
 * buttons.
 *
 * A leading severity icon is derived from the dialog title so an
 * "Error" alert still feels visually distinct from an "Info" alert
 * without breaking the unified blue branding.
 *
 * Closing with `true` confirms the primary action; clicking X or
 * the secondary button (or any other close path) returns `undefined`,
 * which MatNotificationService maps to `false`.
 */
@Component({
  selector: 'feature-mat-dialog-alert',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule],
  templateUrl: './mat-dialog-alert.component.html',
  styleUrls: ['./mat-dialog-alert.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MatDialogAlertComponent {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: MatDialogAlertData,
    public dialogRef: MatDialogRef<MatDialogAlertComponent>
  ) {}

  /**
   * Material icon name shown to the left of the title. Derived
   * from the dialog title so we don't require every call site to
   * pass an explicit severity field — `showAlert('Error', ...)` and
   * `showAlert('Info', ...)` naturally pick the correct glyph.
   * @returns {string} - Material icon ligature.
   */
  get severityIcon(): string {
    switch (this.severity) {
      case 'error':
        return 'error_outline';
      case 'warning':
        return 'warning_amber';
      case 'success':
        return 'check_circle_outline';
      case 'info':
      default:
        return 'info_outline';
    }
  }

  /**
   * Optional CSS hook applied to the header element so theming
   * tweaks (e.g. a red ring on errors) can target a specific
   * severity without changing the component template. The
   * baseline blue background stays intact regardless of severity
   * to preserve visual consistency with the other dialogs.
   * @returns {string} - Severity-prefixed class name.
   */
  get severityHeaderClass(): string {
    return `severity-${this.severity}`;
  }

  /**
   * Close the dialog with `true` to signal that the primary action
   * was confirmed. Mirrors the previous component's API so callers
   * that destructure the boolean continue to work.
   * @param {boolean} isSubmit - Reserved; kept for API stability.
   * @returns {void}
   */
  onSubmit(isSubmit: boolean): void {
    this.dialogRef.close(isSubmit);
  }

  /**
   * Close the dialog without a confirmation. Returns `undefined`
   * which the service maps to `false` via `res === true`.
   * @returns {void}
   */
  onClose(): void {
    this.dialogRef.close();
  }

  /**
   * Map a free-form title into a known severity. Case-insensitive
   * keyword match keeps the legacy API (`title: string`) intact —
   * no caller needs to pass an explicit severity field.
   * @returns {DialogSeverity} - The detected severity bucket.
   */
  private get severity(): DialogSeverity {
    const title = (this.data?.title ?? '').toLowerCase();

    if (title.includes('error') || title.includes('fail')) {
      return 'error';
    }

    if (title.includes('warning') || title.includes('warn')) {
      return 'warning';
    }

    if (title.includes('success')) {
      return 'success';
    }

    return 'info';
  }
}
